"use client"

import { CookiesEnum } from "@/app/enums/Cookies"
import { getCookie } from "@/app/utils/cookie-utils"
import { useEffect, useState } from "react"

export interface UserTrackingData {
    userToken: string | null
    entryPage: string | null
    referrer: string | null
}

/**
 * Hook to access user tracking information
 * Returns the user tracking token and visit data from individual cookies
 */
export function useUserTracking(): UserTrackingData {
    const [trackingData, setTrackingData] = useState<UserTrackingData>({
        userToken: null,
        entryPage: null,
        referrer: null,
    })

    useEffect(() => {
        try {
            // Read individual tracking cookies (simplified approach)
            const userToken = getCookie(CookiesEnum.USER_TRACKING_TOKEN)
            const entryPage = getCookie(CookiesEnum.USER_ENTRY_PAGE)
            const referrer = getCookie(CookiesEnum.USER_REFERER)

            setTrackingData({
                userToken,
                entryPage,
                referrer,
            })
        } catch (error) {
            console.error("Error reading user tracking data:", error)
        }
    }, [])

    return trackingData
}

/**
 * Get just the user token (simplified version)
 * Performance optimized - reads from simple string cookie
 */
export function useUserToken(): string | null {
    const [userToken, setUserToken] = useState<string | null>(null)

    useEffect(() => {
        try {
            // Fast path: read directly from simple token cookie
            const token = getCookie(CookiesEnum.USER_TRACKING_TOKEN)
            setUserToken(token)
        } catch (error) {
            console.error("Error reading user token:", error)
            setUserToken(null)
        }
    }, [])

    return userToken
}
